import { useQueries, useQuery } from '@tanstack/react-query';
import {
  approvalChainQuery,
  approvalChainsQuery,
  type ApprovalChain,
} from '@/entities/approval-chain';

export type ProjectApprovalChains =
  | { kind: 'loading' }
  | { kind: 'failed' }
  | { kind: 'ready'; chains: ApprovalChain[] };

/**
 * Every approval chain in the project with the people its steps name, which only a chain's
 * own read carries. Read as soon as the caller can change members, so the answer is usually in
 * before a change is staged; the reads share the chains drawer's cache.
 */
export function useProjectApprovalChains(
  platformProjectId: string,
  enabled: boolean,
): ProjectApprovalChains {
  const listOptions = approvalChainsQuery(platformProjectId);
  const listQuery = useQuery({ ...listOptions, enabled: listOptions.enabled && enabled });
  const chainIds = listQuery.data?.items.map((chain) => chain.id) ?? [];
  const chainQueries = useQueries({
    queries: chainIds.map((chainId) => {
      const options = approvalChainQuery(platformProjectId, chainId);
      return { ...options, enabled: options.enabled && enabled };
    }),
  });

  if (listQuery.isError || chainQueries.some((query) => query.isError)) return { kind: 'failed' };
  const chains = chainQueries.flatMap((query) => (query.data ? [query.data] : []));
  if (!listQuery.data || chains.length < chainIds.length) return { kind: 'loading' };
  return { kind: 'ready', chains };
}

export type RoleAssignmentsRead = 'ready' | 'loading' | 'unavailable';

/**
 * A step that names a role is counted from the members' role assignments, so without them the
 * check cannot tell whether a change leaves it short and must not report that nothing is.
 */
export function checkableWithRoleAssignments(
  approvalChains: ProjectApprovalChains,
  roleAssignments: RoleAssignmentsRead,
): ProjectApprovalChains {
  if (approvalChains.kind !== 'ready' || roleAssignments === 'ready') return approvalChains;
  const namesRole = approvalChains.chains.some((chain) =>
    (chain.steps ?? []).some((step) => step.approverRole),
  );
  if (!namesRole) return approvalChains;
  return roleAssignments === 'loading' ? { kind: 'loading' } : { kind: 'failed' };
}
